import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Combobox, Dialog, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TWclassNames } from "../../utils/Div";
import { getExampleQueries, searchGlobal } from "./globalSearchService";

const MODULE_ORDER = ["customer", "project", "report", "case", "hse_document", "contact"];
const SEARCH_DEBOUNCE_MS = 300;

function moduleLabel(t, module) {
  const keys = {
    customer: "Customer",
    report: "Report",
    project: "Project",
    case: "Case",
    contact: "Contact",
    hse_document: "globalSearch.module.hse_document",
  };
  const key = keys[module] || module;
  return key.startsWith("globalSearch.") ? t(key) : t(key);
}

function groupResults(results, t) {
  const groups = {};
  for (const item of results) {
    if (!groups[item.module]) {
      groups[item.module] = {
        module: item.module,
        label: moduleLabel(t, item.module),
        items: [],
      };
    }
    groups[item.module].items.push(item);
  }
  return MODULE_ORDER.filter((m) => groups[m]).map((m) => groups[m]);
}

function isMacPlatform() {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform);
}

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [statusLine, setStatusLine] = useState(null);
  const [suggestions, setSuggestions] = useState(() => getExampleQueries(t));

  const shortcutLabel = isMacPlatform() ? "⌘K" : "Ctrl+K";

  useEffect(() => {
    const handler = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;

    if (!debouncedQuery) {
      setResults([]);
      setStatusLine(null);
      setSuggestions(getExampleQueries(t));
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    searchGlobal(debouncedQuery, t)
      .then((response) => {
        if (cancelled) return;
        setResults(response.results || []);
        setStatusLine(response.statusLine || null);
        setSuggestions(response.suggestions || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Global search error:", err);
        if (cancelled) return;
        setResults([]);
        setStatusLine(t("globalSearch.error"));
        setSuggestions([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open, t]);

  const groupedResults = useMemo(
    () => groupResults(results, t),
    [results, t]
  );

  const handleSelect = useCallback(
    (item) => {
      if (!item?.href) return;
      setOpen(false);
      navigate(item.href);
    },
    [navigate]
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const showEmptyHints = !query.trim() && !loading;
  const showNoResults =
    debouncedQuery && !loading && results.length === 0 && statusLine;
  const showResults = results.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        title={t("globalSearch.trigger_label")}
        aria-label={t("globalSearch.trigger_label")}
        className="hidden h-10 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500 transition hover:border-slate-300 hover:bg-white hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 lg:inline-flex"
      >
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="max-w-[140px] truncate">{t("globalSearch.trigger_short")}</span>
        <kbd className="hidden h-5 items-center rounded border border-slate-200 bg-white px-1.5 font-mono text-[10px] font-medium text-slate-400 xl:inline-flex">
          {shortcutLabel}
        </kbd>
      </button>

      <button
        type="button"
        onClick={handleOpen}
        title={t("globalSearch.trigger_label")}
        aria-label={t("globalSearch.trigger_label")}
        className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 lg:hidden"
      >
        <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      <Transition.Root
        show={open}
        as={Fragment}
        afterLeave={() => {
          setQuery("");
          setDebouncedQuery("");
          setResults([]);
          setStatusLine(null);
          setSuggestions(getExampleQueries(t));
        }}
      >
        <Dialog as="div" className="relative z-[400]" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/25 backdrop-blur-[1px]" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 pt-[12vh] sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-2xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all">
                <Combobox onChange={handleSelect} nullable>
                  <div className="relative border-b border-gray-100">
                    <MagnifyingGlassIcon
                      className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                    <Combobox.Input
                      autoFocus
                      className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-base text-gray-900 placeholder:text-gray-400 focus:ring-0"
                      placeholder={t("globalSearch.input_placeholder")}
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      aria-label={t("globalSearch.input_label")}
                    />
                    {loading && (
                      <span className="absolute right-4 top-3.5 inline-flex items-center gap-2 text-xs text-gray-400">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-sky-500" />
                        {t("globalSearch.searching")}
                      </span>
                    )}
                  </div>

                  <div className="max-h-[min(60vh,420px)] overflow-y-auto">
                    {statusLine && showResults && (
                      <p className="border-b border-gray-50 px-4 py-2 text-xs text-gray-500">
                        {statusLine}
                      </p>
                    )}

                    {showResults && (
                      <Combobox.Options static className="py-2">
                        {groupedResults.map((group) => (
                          <li key={group.module}>
                            <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                              {group.label}
                            </div>
                            <ul className="text-sm text-gray-800">
                              {group.items.map((item) => (
                                <Combobox.Option
                                  key={`${item.module}-${item.id}`}
                                  value={item}
                                  className={({ active }) =>
                                    TWclassNames(
                                      "cursor-pointer select-none px-4 py-2.5",
                                      active ? "bg-sky-600 text-white" : "text-gray-800"
                                    )
                                  }
                                >
                                  {({ active }) => (
                                    <div className="flex flex-col gap-0.5">
                                      <span className="truncate font-medium">
                                        {item.title}
                                      </span>
                                      {item.meta?.length > 0 && (
                                        <span
                                          className={TWclassNames(
                                            "truncate text-xs",
                                            active ? "text-sky-100" : "text-gray-500"
                                          )}
                                        >
                                          {item.meta.join(" · ")}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </Combobox.Option>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </Combobox.Options>
                    )}

                    {showEmptyHints && (
                      <div className="px-4 py-5">
                        <p className="text-sm font-medium text-gray-900">
                          {t("globalSearch.empty_title")}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {t("globalSearch.empty_hint")}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {suggestions.map((suggestion) => (
                            <button
                              key={suggestion}
                              type="button"
                              onClick={() => setQuery(suggestion)}
                              className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {showNoResults && (
                      <div className="px-4 py-8 text-center text-sm text-gray-500">
                        {statusLine}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
                    <span>{t("globalSearch.footer_navigate")}</span>
                    <span>{t("globalSearch.footer_open")}</span>
                    <span className="ml-auto hidden sm:inline">
                      {t("globalSearch.footer_shortcut", { shortcut: shortcutLabel })}
                    </span>
                  </div>
                </Combobox>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

export default GlobalSearch;
